// Port Management module internationalization - English
export default {
  routeSelection: {
    ambiguousLabel: 'Needs review',
    firstPlaceholder: 'Select first-level route',
    secondPlaceholder: 'Select second-level route',
    ambiguous: 'Multiple route paths exist. Please confirm and select a route. Unchanged relations will be preserved.',
    invalid: 'Legacy relations with missing or incompatible types are excluded from selection. Existing data is preserved unless you change the route.',
    loadFailed: 'Failed to load routes. Reopen the editor to retry. Existing relations will not be cleared.',
    saveFailed: 'Failed to save route relations. Review and retry; the save is incomplete.'
  },
  title: 'Port Management',

  // Tabs
  tabs: {
    seaport: 'Seaport',
    riverPort: 'River Port',
    inlandPoint: 'Inland Point',
    portGroup: 'Port Group',
    airport: 'Airport',
    railwayStation: 'Railway Station'
  },

  // Search filter area
  search: {
    keyword: 'Keyword Search',
    keywordPlaceholder: 'Port name, UN Location',
    airportKeywordPlaceholder: 'Airport name, IATA code',
    country: 'Country/Region',
    countryPlaceholder: 'Select country',
    isBasePort: 'Is Base Port',
    isBasePortPlaceholder: 'Select type',
    status: 'Status',
    statusPlaceholder: 'Select status',
    yes: 'Yes',
    no: 'No',
    enabled: 'Enabled',
    disabled: 'Disabled',
    searchButton: 'Search',
    resetButton: 'Reset'
  },

  // Action buttons
  actions: {
    addSeaport: 'Add Seaport',
    addAirport: 'Add Airport',
    addCurrentPort: 'Add {type}',
    copySeaportFromPlatform: 'Copy Platform Seaport',
    copyCurrentPortFromPlatform: 'Copy Platform {type}',
    copyAllCurrentPortFromPlatform: 'Copy All Platform {type}',
    copyAirportFromPlatform: 'Copy Platform Airport',
    edit: 'Edit',
    ediSetting: 'EDI Code Setting',
    batchEnable: 'Batch Enable',
    batchDisable: 'Batch Disable',
    enableItem: 'Enable',
    disableItem: 'Disable',
    recordsCount: 'Total {count} records'
  },

  // Table columns
  table: {
    portNameCn: 'Port Name (Chinese)',
    portNameEn: 'Port Name (English)',
    airportNameCn: 'Airport Name (Chinese)',
    airportNameEn: 'Airport Name (English)',
    unLocation: 'UN Location',
    iataCode: 'IATA Code',
    country: 'Country/Region',
    countryIsoCode: 'Country ISO Code',
    province: 'State/Province',
    city: 'City',
    cityCn: 'City (Chinese)',
    cityEn: 'City (English)',
    portType: 'Port Type',
    amsPortCode: 'AMS Port Code',
    isBasePort: 'Is Base Port',
    relatedRoutes: 'Related Routes',
    level1Route: 'Level 1 Route',
    level2Route: 'Level 2 Route',
    relatedTerminals: 'Related Terminals',
    ediCodes: 'EDI Codes',
    longitude: 'Longitude',
    latitude: 'Latitude',
    status: 'Status',
    operation: 'Operation'
  },

  // Modals
  modal: {
    // Edit modal
    editSeaport: 'Edit Seaport',
    addSeaport: 'Add Seaport',
    editCurrentPort: 'Edit {type}',
    addCurrentPort: 'Add {type}',
    editAirport: 'Edit Airport',
    addAirport: 'Add Airport',
    basicInfo: 'Basic Information',
    editableInfo: 'Editable Information',

    // Selection modal
    selectSeaportTitle: 'Copy Platform Seaport',
    selectCurrentPortTitle: 'Copy Platform {type}',
    selectAirportTitle: 'Copy Platform Airport',
    confirmAddSeaport: 'Copy {count} Seaports',
    confirmAddCurrentPort: 'Copy {count} {type}',
    confirmAddAirport: 'Copy {count} Airports',
    cancel: 'Cancel',
    selected: 'Selected {count} items, {total} available',
    selectedAllSummary: 'Selected {count} items, {total} available',
    selectAllFiltered: 'Select all {total} copyable matching records',
    allFilteredSelectedHint: 'All {total} records matching the current filters are selected; {excluded} excluded',
    clearSelected: 'Clear',
    selectedPreview: 'Selected Details',
    excludedPreview: 'Excluded Details',
    selectedDetailCount: '{count} items',
    selectedEmpty: 'No records selected. Select records from the platform table on the right.',
    platformDataTitle: 'Platform Data',
    availableTotal: '{total} available',
    platformTotal: '{total} platform records, {available} copyable',
    querySelectedNotice: 'All {total} copyable records matching the current filters are selected. The left table does not list every record; uncheck rows on the right to exclude them. {excluded} excluded.',
    copyStatus: 'Copy Status',
    copyStatusAvailable: 'Available',
    copyStatusExists: 'Existing',
    copyStatusAll: 'All',
    copyStatusPlaceholder: 'Select copy status',
    syncLimitNotice: '{count} records selected, exceeding the synchronous copy limit of {limit}. Please narrow the filters and try again.',

    // EDI setting modal
    ediTitle: 'EDI Code Setting',
    carrier: 'Carrier',
    ediCode: 'EDI Code',
    addEdiCode: 'Add EDI Code',
    deleteEdiCode: 'Delete',
    duplicateWarning: 'Duplicate',
    duplicateAlert: 'Duplicate carrier+EDI code combinations detected, please check rows {rows}'
  },

  // Confirmation dialogs
  confirm: {
    enableSeaport: 'Are you sure to enable this port?',
    disableSeaport: 'Are you sure to disable this port?',
    enableCurrentPort: 'Are you sure to enable this {type}?',
    disableCurrentPort: 'Are you sure to disable this {type}?',
    copyAllCurrentPortTitle: 'Copy All Platform {type}',
    copyAllCurrentPortContent: 'Copy all available platform {type} records to the current tenant? Existing records will be skipped and the task will run in the background.',
    copyAllCurrentPortOk: 'Create Task',
    copyWillCreateTaskTitle: 'Create Background Copy Task',
    copyWillCreateTaskContent: '{count} {type} records are selected. Submitting will create a background task, and details can be viewed in Task Management.',
    copyWillCreateTaskOk: 'Create Task',
    copyTaskRedirectTitle: 'Task Submitted',
    copyTaskRedirectContent: 'The copy task has been added to Task Management. You can go there to view progress and results.',
    copyTaskRedirectOk: 'Go to Task Management',
    suspectedDuplicateTitle: 'Possible Duplicate Port',
    suspectedDuplicateNameCnCountry: 'The Chinese name and country ISO code match an existing port.',
    suspectedDuplicateNameEnCountry: 'The English name and country ISO code match an existing port.',
    suspectedAirportDuplicateTitle: 'Possible Duplicate Airport',
    suspectedAirportDuplicateNameCnCountry: 'The Chinese name and country ISO code match an existing airport.',
    suspectedAirportDuplicateNameEnCountry: 'The English name and country ISO code match an existing airport.',
    suspectedDuplicateOk: 'Continue Saving',
    suspectedDuplicateCancel: 'Go Back',
    enableAirport: 'Are you sure to enable this airport?',
    disableAirport: 'Are you sure to disable this airport?'
  },

  // Form related
  form: {
    portNameEn: 'Port Name (English)',
    portNameCn: 'Port Name (Chinese)',
    airportNameEn: 'Airport Name (English)',
    airportNameCn: 'Airport Name (Chinese)',
    unLocation: 'UN Location',
    iataCode: 'IATA Code',
    country: 'Country/Region',
    countryIsoCode: 'Country ISO Code',
    province: 'State/Province',
    city: 'City',
    cityCn: 'City (Chinese)',
    cityEn: 'City (English)',
    portType: 'Port Type',
    amsPortCode: 'AMS Port Code',
    longitude: 'Longitude',
    latitude: 'Latitude',
    isBasePort: 'Is Base Port',
    relatedRoutes: 'Related Routes',
    relatedTerminals: 'Related Terminals',

  // Form validation
    portNameEnRequired: 'Please enter port English name',
    portNameCnRequired: 'Please enter port Chinese name',
    airportNameEnRequired: 'Please enter airport English name',
    airportNameCnRequired: 'Please enter airport Chinese name',
    unLocationRequired: 'Please enter UN Location',
    iataCodeRequired: 'Please enter IATA code',
    countryRequired: 'Please select country',
    amsPortCodeRequired: 'Please enter AMS port code',
    longitudeRequired: 'Please enter longitude',
    latitudeRequired: 'Please enter latitude',
    carrierRequired: 'Please select carrier',
    ediCodeRequired: 'Please enter EDI code',

    // Form placeholders
    portNameEnPlaceholder: 'Please enter port English name',
    portNameCnPlaceholder: 'Please enter port Chinese name',
    airportNameEnPlaceholder: 'Please enter airport English name',
    airportNameCnPlaceholder: 'Please enter airport Chinese name',
    unLocationPlaceholder: 'Please enter UN Location code',
    iataCodePlaceholder: 'Please enter IATA code, e.g.: PVG',
    countryPlaceholder: 'Please select country',
    countryIsoCodePlaceholder: 'Please enter country ISO code',
    provincePlaceholder: 'Enter state/province',
    cityPlaceholder: 'Enter city',
    portTypePlaceholder: 'Please enter port type',
    amsPortCodePlaceholder: 'Please enter AMS port code',
    longitudePlaceholder: 'Please enter longitude, e.g.: 121.4737',
    latitudePlaceholder: 'Please enter latitude, e.g.: 31.2304',
    relatedRoutesPlaceholder: 'Please select related routes',
    relatedTerminalsPlaceholder: 'Please select related terminals',
    carrierPlaceholder: 'Select carrier',
    ediCodePlaceholder: 'Enter EDI code'
  },

  // Message prompts
  messages: {
    // Selection prompts
    selectSeaportToEnable: 'Please select seaports to enable',
    selectSeaportToDisable: 'Please select seaports to disable',
    selectCurrentPortToEnable: 'Please select {type} records to enable',
    selectCurrentPortToDisable: 'Please select {type} records to disable',
    selectAirportToEnable: 'Please select airports to enable',
    selectAirportToDisable: 'Please select airports to disable',
    selectPortsToAdd: 'Please select seaports to copy',
    selectCurrentPortsToAdd: 'Please select {type} records to copy',
    selectAirportsToAdd: 'Please select airports to copy',

    // Operation success
    seaportEnabled: 'Seaport enabled',
    seaportDisabled: 'Seaport disabled',
    currentPortEnabled: '{type} enabled',
    currentPortDisabled: '{type} disabled',
    airportEnabled: 'Airport enabled',
    airportDisabled: 'Airport disabled',
    seaportBatchEnabled: 'Enabled {count} seaports',
    seaportBatchDisabled: 'Disabled {count} seaports',
    currentPortBatchEnabled: 'Enabled {count} {type} records',
    currentPortBatchDisabled: 'Disabled {count} {type} records',
    airportBatchEnabled: 'Enabled {count} airports',
    airportBatchDisabled: 'Disabled {count} airports',
    seaportAssociateSuccess: 'Copied {count} seaports',
    currentPortAssociateSuccess: 'Copied {count} {type} records',
    airportAssociateSuccess: 'Copied {count} airports',
    copyTaskSubmitted: 'Created background copy task for {type} (Task Center ID: {taskId}). About {count} records will be copied. View details in Task Management.',
    copyTaskAlreadyRunning: 'A {type} copy task is already running (Task Center ID: {taskId}). View details in Task Management.',
    editSuccess: 'Edit successful',
    addSuccess: 'Add successful',
    unLocationDuplicate: 'UN Location already exists and cannot be saved',
    iataCodeDuplicate: 'IATA code already exists and cannot be saved',
    duplicateCheckError: 'Port duplicate check failed, please try again later',
    ediSettingSuccess: 'EDI code setting successful',
    ediClearedSuccess: 'EDI codes cleared',
    associationSaveSuccess: 'Association information saved successfully',
    partialAssociationSave: 'Partial association information saved successfully',
    noAssociationToSave: 'No association information to save',

    // Operation failures
    statusToggleError: 'Failed to toggle status, please try again later',
    seaportBatchEnableError: 'Failed to batch enable, please try again later',
    seaportBatchDisableError: 'Failed to batch disable, please try again later',
    currentPortBatchEnableError: 'Failed to batch enable {type}, please try again later',
    currentPortBatchDisableError: 'Failed to batch disable {type}, please try again later',
    airportBatchEnableError: 'Failed to batch enable, please try again later',
    airportBatchDisableError: 'Failed to batch disable, please try again later',
    seaportAssociateError: 'Failed to copy seaports, please try again later',
    currentPortAssociateError: 'Failed to copy {type}, please try again later',
    airportAssociateError: 'Failed to copy airports, please try again later',
    copyTaskSubmitError: 'Failed to create background copy task for {type}, please try again later',
    editError: 'Edit failed',
    addError: 'Add failed',
    ediSettingError: 'EDI code setting failed',
    associationSaveError: 'Failed to save association information, please try again later',

    // Data loading failures
    seaportLoadError: 'Failed to load seaport data, please try again later',
    currentPortLoadError: 'Failed to load {type} data, please try again later',
    airportLoadError: 'Failed to load airport data, please try again later',
    platformSeaportLoadError: 'Failed to load platform seaports, please try again later',
    platformCurrentPortLoadError: 'Failed to load platform {type}, please try again later',
    platformAirportLoadError: 'Failed to load platform airports, please try again later',
    countryLoadError: 'Failed to load country data, using default data',
    carrierLoadError: 'Failed to load carrier data, using default data',
    routeLoadError: 'Failed to load route data, using default data',

    // Duplicate data prompts
    duplicateEdiCombination: 'Duplicate carrier+EDI code combinations in rows {rows}, please check and retry',
    duplicateEdiCheck: 'Duplicate carrier+EDI code combinations in rows {rows}, please handle duplicates first'
  },

  // Status text
  statusText: {
    enabled: 'enabled',
    disabled: 'disabled',
    yes: 'Yes',
    no: 'No',
    notSet: 'Not Set',
    codesCount: '{count} codes'
  },

  railwayStation: {
    search: {
      keywordPlaceholder: 'Railway code / Chinese name / English name / alias',
      railwayCarrierPlaceholder: 'Railway company',
      countryPlaceholder: 'Country',
      stationTypePlaceholder: 'Station type',
      operationStatusPlaceholder: 'Operation status',
      enabledPlaceholder: 'Enable status',
      search: 'Search',
      reset: 'Reset'
    },
    actions: {
      add: 'Add Railway Station',
      view: 'View',
      enable: 'Enable',
      disable: 'Disable',
      delete: 'Delete',
      batchEnable: 'Batch Enable',
      batchDisable: 'Batch Disable',
      batchDelete: 'Batch Delete'
    },
    table: {
      stationCode: 'Railway Code',
      name: 'Station Name',
      railwayCarrier: 'Railway Company',
      location: 'Country/State',
      railwayLineName: 'Physical Line',
      belongingChannel: 'Channel',
      trackGauge: 'Track Gauge',
      stationType: 'Station Type',
      operationStatus: 'Operation Status',
      operationDirection: 'Operation Direction',
      borderChannelType: 'Border Channel Type',
      gaugeChange: 'Gauge Change',
      containerTypes: 'Supported Container Types',
      enabled: 'Enable Status',
      operations: 'Operations'
    },
    detail: {
      title: 'Railway Station Details',
      stationCode: 'Railway Code',
      nameCn: 'Standard Chinese Name',
      nameEn: 'Standard English Name',
      aliasName: 'Alias/Short Name',
      railwayCarrier: 'Railway Company',
      location: 'Country/State',
      railwayLineName: 'Physical Line',
      belongingChannel: 'Channel',
      trackGauge: 'Track Gauge',
      stationType: 'Station Type',
      operationStatus: 'Operation Status',
      operationDirection: 'Operation Direction',
      borderChannelType: 'Border Channel Type',
      gaugeChange: 'Gauge Change',
      foreignStationName: 'Foreign Station',
      foreignUicCode: 'Foreign UIC Code',
      customsSupervisionCode: 'Customs Supervision Code',
      coordinate: 'Coordinates',
      maxLoadLimit: 'Max Load Limit',
      containerTypes: 'Supported Container Types'
    },
    options: {
      enabled: 'Enabled',
      disabled: 'Disabled',
      no: 'No',
      stationType: {
        marshalling: 'Marshalling Station',
        freight: 'Freight Station',
        passenger: 'Passenger Station',
        border: 'Border Station',
        privateLine: 'Private Siding',
        boundary: 'Boundary Station'
      },
      operationStatus: {
        enabled: 'Enabled',
        suspended: 'Temporarily Closed',
        decommissioned: 'Decommissioned',
        migrated: 'Renamed/Migrated'
      },
      operationDirection: {
        both: 'Both',
        outboundOnly: 'Outbound Only',
        inboundOnly: 'Inbound Only'
      },
      borderChannelType: {
        east: 'East Channel',
        middle: 'Middle Channel',
        west: 'West Channel',
        north: 'North Line',
        south: 'South Line'
      }
    },
    modal: {
      title: 'Add Railway Station',
      confirmText: 'Confirm Add ({count})',
      selected: '{count} selected, {total} available',
      searchPlaceholder: 'Railway code / Chinese name / English name / alias'
    },
    confirm: {
      batchDelete: 'Delete selected railway stations?',
      enable: 'Enable this railway station?',
      disable: 'Disable this railway station?',
      delete: 'Delete this railway station?'
    },
    messages: {
      loadError: 'Failed to load railway stations',
      platformLoadError: 'Failed to load platform railway stations',
      selectToAssociate: 'Select railway stations to add',
      associateSuccess: 'Added {count} railway stations',
      associateError: 'Failed to add railway stations',
      enableSuccess: 'Enabled successfully',
      disableSuccess: 'Disabled successfully',
      updateStatusError: 'Failed to update status',
      deleteSuccess: 'Deleted successfully',
      deleteError: 'Failed to delete railway station'
    },
    stats: {
      total: 'Total {total} records'
    },
    format: {
      gaugeChange: 'Gauge change {count} times',
      maxLoadLimit: '{value} tons'
    }
  },

  // Port Group Management
  portGroup: {
    title: 'Port Group Management',
    search: {
      keyword: 'Keyword',
      keywordPlaceholder: 'Search by name or code',
      status: 'Status',
      statusPlaceholder: 'Select status',
      searchButton: 'Search',
      resetButton: 'Reset'
    },
    table: {
      code: 'Group Code',
      nameCn: 'Chinese Name',
      nameEn: 'English Name',
      portCount: 'Port Count',
      status: 'Status',
      createdTime: 'Created Time',
      operations: 'Actions',
      recordsCount: '{count} records'
    },
    actions: {
      create: 'Create',
      createSelf: 'Create Port Group',
      copyFromPlatform: 'Copy from Platform',
      edit: 'Edit',
      delete: 'Delete'
    },
    form: {
      title: {
        create: 'Create Port Group',
        edit: 'Edit Port Group'
      },
      code: 'Group Code',
      codePlaceholder: 'Enter group code (e.g. PG001)',
      codeRequired: 'Group code is required',
      nameCn: 'Chinese Name',
      nameCnPlaceholder: 'Enter Chinese name',
      nameCnRequired: 'Chinese name is required',
      nameEn: 'English Name',
      nameEnPlaceholder: 'Enter English name',
      portCodes: 'Associated Ports',
      portCodesPlaceholder: 'Select ports',
      portCodesRequired: 'At least one port is required',
      routes: 'Associated Routes',
      routesPlaceholder: 'Select routes (optional)',
      enabled: 'Status',
      cancel: 'Cancel',
      save: 'Save'
    },
    tooltip: {
      portList: 'Port Details'
    },
    confirm: {
      deleteTitle: 'Confirm Delete',
      deleteContent: 'Delete port group "{name}" ({code})?',
      deleteWarning: 'This group is linked to {count} freight rates. Deleting will clear the group reference in those rates.',
      deleteOk: 'Delete',
      deleteCancel: 'Cancel'
    },
    modal: {
      copyPlatformTitle: 'Copy Platform Port Groups',
      confirmCopy: 'Confirm Copy ({count})',
      copyStatus: 'Status',
      copyStatusPlaceholder: 'Filter by status',
      copyStatusAvailable: 'Available',
      copyStatusExists: 'Copied',
      copyStatusAll: 'All',
      selectedPreview: 'Selected Preview',
      selectedCount: '{count} selected',
      clearSelected: 'Clear All',
      selectedEmpty: 'Please select port groups from the right list',
      platformDataTitle: 'Platform Port Groups',
      platformTotal: 'Total {total} items',
      selectAllFiltered: 'Select All ({total})',
      copySuccess: 'Copy successful',
      copyError: 'Copy failed'
    },
    messages: {
      loadError: 'Failed to load port group list',
      loadDetailError: 'Failed to load port group details',
      loadPlatformError: 'Failed to load platform port groups',
      saveSuccess: 'Save successful',
      saveError: 'Save failed',
      deleteSuccess: 'Delete successful',
      deleteError: 'Delete failed',
      selectWarning: 'Please select port groups to copy'
    }
  }
}
